import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import UnderwritingReport from "./UnderwritingReport";
import { TACTIC_ORDER_ES, techniqueEs, tacticsEs } from "../i18n/frameworksEs";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Import useAuth hook - assuming it's exported from App.js or separate file
// If ExecutiveReport is inside App.js context, we can access it directly
// Otherwise we'll use a workaround

// Color palette for UI
const COLORS = {
  critical: '#ef4444',  // red-500
  high: '#f97316',      // orange-500
  medium: '#eab308',    // yellow-500
  low: '#3b82f6',       // blue-500
  info: '#6b7280',      // gray-500
  
  // Insurability
  green: '#22c55e',     // green-500 - ASEGURABLE
  yellow: '#eab308',    // yellow-500 - CON RESTRICCIONES
  red: '#ef4444',       // red-500 - NO ASEGURABLE
};

const ExecutiveReport = ({ setActiveTab: setParentActiveTab, VerModule}) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('technical');
  const [pymeLoading, setPymeLoading] = useState(false);

  const handlePyMEReport = async () => {
    setPymeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pyme-report/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `informe_pyme_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Error generando informe PyME: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPymeLoading(false);
    }
  }; // 'technical' | 'insurance' | 'posture' | 'ai-defense' | 'microsegmentation' | 'usb-policy'
  const [currentUser, setCurrentUser] = useState(null); // Current user from API
  
  // Security Posture Manual Validations
  const [manualValidations, setManualValidations] = useState({
    antivirus_manually_confirmed: false,
    antivirus_manual_details: '',
    firewall_manually_confirmed: false,
    firewall_manual_details: '',
    vpn_manually_confirmed: false,
    vpn_manual_details: '',
    auditor_notes: ''
  });
  const [savingValidations, setSavingValidations] = useState(false);
  
  useEffect(() => {
    fetchCurrentUser();
    fetchReportData();
    fetchManualValidations();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };
  
  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.get(`${API}/executive-report/consolidated`, { params });
      setReportData({
        ...response.data,
        vulnerability_summary: {
          critical: response.data.critical_vulnerabilities || 0,
          high: response.data.high_vulnerabilities || 0,
          medium: response.data.medium_vulnerabilities || 0,
          low: response.data.low_vulnerabilities || 0,
          total: response.data.total_vulnerabilities || 0,
          top_critical: response.data.top_10_critical || []
        },
        osint_summary: {
          subdomains_count: response.data.exposed_subdomains_count || 0,
          exposed_emails_count: response.data.exposed_emails_count || 0,
          total_scans: response.data.osint_scans_count || 0
        },
        web_summary: {
          average_score: response.data.average_web_security_score || 0,
          total_scans: response.data.web_scans_count || 0,
          scores_by_target: response.data.web_scores_by_target || [],
          targets_tested: response.data.web_scores_by_target?.length || 0
        },
        network_summary: {
          high_risk_ports: response.data.high_risk_ports_count || 0,
          medium_risk_ports: response.data.medium_risk_ports_count || 0,
          smb_anonymous_access: response.data.smb_anonymous_access || false,
          nfs_world_writable: response.data.nfs_world_writable || false,
          total_scans: response.data.network_scans_count || 0
        },
        internal_agent_summary: response.data.internal_agent ? {
          total_vulnerabilities: response.data.internal_agent.total_vulnerabilities || 0,
          critical_count: response.data.internal_agent.critical_count || 0,
          high_count: response.data.internal_agent.high_count || 0,
          medium_count: response.data.internal_agent.medium_count || 0,
          low_count: response.data.internal_agent.low_count || 0,
          info_count: response.data.internal_agent.info_count || 0,
          average_risk_score: response.data.internal_agent.average_risk_score || 0,
          total_scans: response.data.internal_agent.total_scans || 0,
          total_agents: response.data.internal_agent.total_agents || 0,
          scanned_hosts: response.data.internal_agent.scanned_hosts || [],
          latest_scan_date: response.data.internal_agent.latest_scan_date || null
        } : null,
        scan_summary: {
          total_scans: response.data.total_scans || 0,
          last_scan_date: response.data.last_scan_date || null
        },
        api_security: response.data.api_security ? {
          target: response.data.api_security.target || "",
          api_security_score: response.data.api_security.api_security_score || 0,
          risk_level: response.data.api_security.risk_level || "N/A",
          endpoints_discovered: response.data.api_security.endpoints_discovered || 0,
          vulnerabilities_found: response.data.api_security.vulnerabilities_found || 0,
          vulnerabilities: response.data.api_security.vulnerabilities || [],
          recommendations: response.data.api_security.recommendations || [],
          owasp_compliance: response.data.api_security.owasp_compliance || {}
        } : null,
        ai_bot_security: response.data.ai_bot_security ? {
          target: response.data.ai_bot_security.target || "",
          ai_bot_security_score: response.data.ai_bot_security.ai_bot_security_score || 0,
          risk_level: response.data.ai_bot_security.risk_level || "N/A",
          risk_categories: response.data.ai_bot_security.risk_categories || {},
          vulnerabilities: response.data.ai_bot_security.vulnerabilities || [],
          recommendations: response.data.ai_bot_security.recommendations || []
        } : null,
        dark_web_security: response.data.dark_web_security ? {
          risk_score: response.data.dark_web_security.risk_score || 0,
          risk_level: response.data.dark_web_security.risk_level || "N/A",
          emails_scanned: response.data.dark_web_security.emails_scanned || 0,
          emails_exposed: response.data.dark_web_security.emails_exposed || 0,
          total_breaches: response.data.dark_web_security.total_breaches || 0,
          last_scan_date: response.data.dark_web_security.last_scan_date || null,
          exposed_data_types: response.data.dark_web_security.exposed_data_types || [],
          recommendations_count: response.data.dark_web_security.recommendations_count || 0
        } : null,
        brand_impersonation: response.data.brand_impersonation ? {
          risk_score: response.data.brand_impersonation.risk_score || 0,
          risk_level: response.data.brand_impersonation.risk_level || "N/A",
          total_variations_checked: response.data.brand_impersonation.total_variations_checked || 0,
          registered_similar_domains: response.data.brand_impersonation.registered_similar_domains || 0,
          active_threats: response.data.brand_impersonation.active_threats || 0,
          suspicious_certificates: response.data.brand_impersonation.suspicious_certificates || 0,
          last_scan_date: response.data.brand_impersonation.last_scan_date || null,
          recommendations_count: response.data.brand_impersonation.recommendations_count || 0
        } : null
      });
    } catch (error) {
      console.error("Error fetching executive report:", error);
      setError(error.response?.data?.detail || "Error al cargar el reporte ejecutivo");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle PDF Export
  const handleExportPDF = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/executive-report/pdf`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `executive_report_${Date.now()}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('✅ PDF descargado exitosamente');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('❌ Error al generar PDF. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper: Get risk level color
  const getRiskLevelColor = (level) => {
    switch(level?.toUpperCase()) {
      case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-300';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'LOW': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const fetchManualValidations = async () => {
    try {
      const response = await axios.get(`${API}/security-posture/manual-validation`);
      setManualValidations(response.data);
    } catch (error) {
      console.error('Error fetching manual validations:', error);
    }
  };

  const saveManualValidations = async () => {
    try {
      setSavingValidations(true);
      await axios.post(`${API}/security-posture/manual-validation`, null, {
        params: {
          antivirus_confirmed: manualValidations.antivirus_manually_confirmed,
          antivirus_details: manualValidations.antivirus_manual_details,
          firewall_confirmed: manualValidations.firewall_manually_confirmed,
          firewall_details: manualValidations.firewall_manual_details,
          vpn_confirmed: manualValidations.vpn_manually_confirmed,
          vpn_details: manualValidations.vpn_manual_details,
          auditor_notes: manualValidations.auditor_notes
        }
      });
      alert('✅ Validaciones guardadas correctamente');
      // Refresh report data to get updated scores
      await fetchReportData();
    } catch (error) {
      console.error('Error saving manual validations:', error);
      alert('❌ Error al guardar validaciones');
    } finally {
      setSavingValidations(false);
    }
  };
  
  // Helper: Get insurability color
  const getInsurabilityColor = (decision) => {
    if (!decision) return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' };
    
    if (decision.includes('NO ASEGURABLE')) {
      return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', badge: 'bg-red-500' };
    } else if (decision.includes('ASEGURABLE CON RESTRICCIONES')) {
      return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', badge: 'bg-yellow-500' };
    } else if (decision.includes('ASEGURABLE')) {
      return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', badge: 'bg-green-500' };
    } else {
      return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', badge: 'bg-red-500' };
    }
  };
  
  // Helper: Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-AR', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  
  // Render Technical Report Tab
  const renderTechnicalReport = () => {
    if (!reportData) return null;
    
    // Prepare vulnerability distribution data for Pie chart
    const vulnDistribution = [
      { name: 'Critical', value: reportData.vulnerability_summary.critical, color: COLORS.critical },
      { name: 'High', value: reportData.vulnerability_summary.high, color: COLORS.high },
      { name: 'Medium', value: reportData.vulnerability_summary.medium, color: COLORS.medium },
      { name: 'Low', value: reportData.vulnerability_summary.low, color: COLORS.low },
    ].filter(v => v.value > 0);
    
    // Prepare compliance scores data for Bar chart
    const complianceData = (reportData.compliance_scores || []).map(cs => {
      const nameMap = {
        'iso27001': 'ISO 27001',
        'owasp_top10': 'OWASP',
        'nist_csf': 'NIST CSF',
        'pci_dss': 'PCI DSS',
        'gdpr': 'GDPR'
      };
      return {
        name: nameMap[cs.standard] || cs.standard,
        score: cs.percentage
      };
    });
    
    return (
      <div className="space-y-6">
        
      {/* Company Selector for SuperAdmin */}
      {isSuperAdmin && (
        <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">🏢 Seleccionar Empresa</h3>
          <select
            value={selectedCompany}
            onChange={(e) => {
              setSelectedCompany(e.target.value);
              fetchReport();
            }}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Mi Empresa (por defecto)</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name} - {company.subscription_plan}
              </option>
            ))}
          </select>
        </div>
      )}

{/* Score Global Card */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Score Global de Seguridad</h3>
              <p className="text-gray-600">Fecha de generación: {formatDate(reportData.generated_at)}</p>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold text-blue-600 mb-2" data-testid="exec-global-score">
                {reportData.has_scan_data === false ? "—" : reportData.global_risk_score}
                <span className="text-3xl text-gray-500">/100</span>
              </div>
              {reportData.score_is_provisional && (
                <span data-testid="exec-score-provisional" className="inline-block mb-2 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                  PROVISIONAL · cobertura {reportData.score_coverage?.core_done ?? 0} de {reportData.score_coverage?.core_total ?? 4}
                </span>
              )}
              <span className={`inline-block px-4 py-2 rounded-full font-semibold border-2 ${getRiskLevelColor(reportData.risk_level)}`}>
                {reportData.risk_level}
              </span>
            </div>
          </div>
          
          {!reportData.score_is_provisional && (reportData.score_coverage?.recommended_missing || []).length > 0 && (
            <div data-testid="exec-score-recommended" className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
              <span className="font-semibold">Puntaje calificable.</span> Para mayor precision se recomienda tambien:{" "}
              {(reportData.score_coverage?.recommended_missing || []).join(" · ")}
            </div>
          )}

          {reportData.score_is_provisional && (
            <div data-testid="exec-score-missing" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <span className="font-semibold">Puntaje provisional.</span> Para una calificación certera faltan estos escaneos:{" "}
              {(reportData.missing_core_assessments || []).join(" · ")}
            </div>
          )}

          {/* Progress bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className={`h-4 rounded-full transition-all duration-500 ${
                  reportData.global_risk_score >= 70 ? 'bg-green-500' :
                  reportData.global_risk_score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${reportData.has_scan_data === false ? 0 : reportData.global_risk_score}%` }}
              ></div>
            </div>
          </div>
        </div>
        
        {/* Vulnerability Distribution & Module Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vulnerability Distribution (Donut Chart) */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Distribución de Vulnerabilidades</h3>
            {vulnDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={vulnDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {vulnDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <div className="text-4xl mb-2">🎉</div>
                <p>No se detectaron vulnerabilidades</p>
              </div>
            )}
            
            {/* Summary counts */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Critical</p>
                <p className="text-2xl font-bold text-red-600">{reportData.vulnerability_summary.critical}</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">High</p>
                <p className="text-2xl font-bold text-orange-600">{reportData.vulnerability_summary.high}</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Medium</p>
                <p className="text-2xl font-bold text-yellow-600">{reportData.vulnerability_summary.medium}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Low</p>
                <p className="text-2xl font-bold text-blue-600">{reportData.vulnerability_summary.low}</p>
              </div>
            </div>
          </div>
          
          {/* Module Metrics (4 cards) */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Métricas por Módulo</h3>
            <div className="space-y-4">
              {/* OSINT */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🔍</span>
                  <h4 className="font-semibold text-gray-900">OSINT Intelligence</h4>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-600">Subdominios</p>
                    <p className="font-bold text-purple-700">{reportData.osint_summary.subdomains_count}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Emails Expuestos</p>
                    <p className="font-bold text-purple-700">{reportData.osint_summary.exposed_emails_count}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-600">Total Scans</p>
                    <p className="font-bold text-purple-700">{reportData.osint_summary.total_scans}</p>
                  </div>
                </div>
              </div>
              
              {/* Web Security */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🌐</span>
                  <h4 className="font-semibold text-gray-900">Web Security Testing</h4>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-600">Avg Score</p>
                    <p className="font-bold text-blue-700">{reportData.web_summary.average_score.toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Total Tests</p>
                    <p className="font-bold text-blue-700">{reportData.web_summary.total_scans}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-600">Objetivos Tested</p>
                    <p className="font-bold text-blue-700">{reportData.web_summary.targets_tested}</p>
                  </div>
                </div>
              </div>
              
              {/* Network Enumeration */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🌐</span>
                  <h4 className="font-semibold text-gray-900">Network Enumeration</h4>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-600">High-Risk Ports</p>
                    <p className="font-bold text-red-700">{reportData.network_summary.high_risk_ports}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Medium-Risk Ports</p>
                    <p className="font-bold text-yellow-700">{reportData.network_summary.medium_risk_ports}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">SMB Anonymous</p>
                    <p className="font-bold text-green-700">{reportData.network_summary.smb_anonymous_access ? 'Sí' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">NFS World-Writable</p>
                    <p className="font-bold text-green-700">{reportData.network_summary.nfs_world_writable ? 'Sí' : 'No'}</p>
                  </div>
                </div>
              </div>
              
              {/* Internal Agent Scanner - Infraestructura Interna */}
              {reportData.internal_agent_summary && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">🖥️</span>
                    <h4 className="font-semibold text-gray-900">Infraestructura Interna (Legacy)</h4>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div>
                      <p className="text-gray-600">Hosts Escaneados</p>
                      <p className="font-bold text-purple-700">{reportData.internal_agent_summary.scanned_hosts?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Vulnerabilidades</p>
                      <p className="font-bold text-red-700">{reportData.internal_agent_summary.total_vulnerabilities}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Críticas</p>
                      <p className="font-bold text-red-700">{reportData.internal_agent_summary.critical_count}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Altas</p>
                      <p className="font-bold text-orange-600">{reportData.internal_agent_summary.high_count}</p>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-600">Risk Score Promedio</p>
                      <p className={`font-bold ${reportData.internal_agent_summary.average_risk_score > 70 ? 'text-red-600' : reportData.internal_agent_summary.average_risk_score > 40 ? 'text-orange-600' : 'text-green-600'}`}>
                        {reportData.internal_agent_summary.average_risk_score}/100
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Último Scan</p>
                      <p className="font-bold text-gray-700">
                        {reportData.internal_agent_summary.latest_scan_date ? 
                          formatDate(reportData.internal_agent_summary.latest_scan_date).split(',')[0] : 'N/A'}
                      </p>
                    </div>
                  </div>
                  {reportData.internal_agent_summary.scanned_hosts?.length > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      Hosts: {reportData.internal_agent_summary.scanned_hosts.join(', ')}
                    </div>
                  )}
                </div>
              )}
              
              {/* API Security Card */}
              {reportData.api_security && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">🔌</span>
                    <h4 className="font-semibold text-blue-900">Seguridad de APIs (OWASP)</h4>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-gray-600">Score</p>
                      <p className={`font-bold ${reportData.api_security.api_security_score >= 70 ? 'text-green-600' : reportData.api_security.api_security_score >= 40 ? 'text-orange-600' : 'text-red-600'}`}>
                        {reportData.api_security.api_security_score}/100
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Endpoints</p>
                      <p className="font-bold text-blue-700">{reportData.api_security.endpoints_discovered}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Vulnerabilidades</p>
                      <p className="font-bold text-red-700">{reportData.api_security.vulnerabilities_found}</p>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Nivel de Riesgo: <span className={`font-semibold ${reportData.api_security.risk_level === 'CRITICAL' ? 'text-red-600' : reportData.api_security.risk_level === 'HIGH' ? 'text-orange-600' : 'text-yellow-600'}`}>{reportData.api_security.risk_level}</span>
                  </div>
                </div>
              )}
              
              {/* AI Bot Security Card */}
              {reportData.ai_bot_security && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">🤖</span>
                    <h4 className="font-semibold text-purple-900">Seguridad de Bots/Chatbots IA</h4>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-gray-600">Score</p>
                      <p className={`font-bold ${reportData.ai_bot_security.ai_bot_security_score >= 70 ? 'text-green-600' : reportData.ai_bot_security.ai_bot_security_score >= 40 ? 'text-orange-600' : 'text-red-600'}`}>
                        {reportData.ai_bot_security.ai_bot_security_score}/100
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Vulnerabilidades</p>
                      <p className="font-bold text-red-700">{reportData.ai_bot_security.vulnerabilities?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Riesgo</p>
                      <p className={`font-bold ${reportData.ai_bot_security.risk_level === 'CRITICAL' ? 'text-red-600' : reportData.ai_bot_security.risk_level === 'HIGH' ? 'text-orange-600' : 'text-yellow-600'}`}>
                        {reportData.ai_bot_security.risk_level}
                      </p>
                    </div>
                  </div>
                  {reportData.ai_bot_security.vulnerabilities?.length > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      Categorías: {Object.keys(reportData.ai_bot_security.risk_categories || {}).filter(k => reportData.ai_bot_security.risk_categories[k] > 0).join(', ') || 'N/A'}
                    </div>
                  )}
                </div>
              )}
              

              {/* Dark Web Security Card */}
              {reportData.dark_web_security && (
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">🌑</span>
                    <h4 className="font-semibold text-gray-100">Exposición Dark Web</h4>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-gray-400">Score</p>
                      <p className={`font-bold ${reportData.dark_web_security.risk_score >= 70 ? "text-green-400" : reportData.dark_web_security.risk_score >= 40 ? "text-orange-400" : "text-red-400"}`}>
                        {reportData.dark_web_security.risk_score}/100
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400">Emails Expuestos</p>
                      <p className={`font-bold ${reportData.dark_web_security.emails_exposed > 0 ? "text-red-400" : "text-green-400"}`}>
                        {reportData.dark_web_security.emails_exposed}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400">Brechas</p>
                      <p className={`font-bold ${reportData.dark_web_security.total_breaches > 0 ? "text-red-400" : "text-green-400"}`}>
                        {reportData.dark_web_security.total_breaches}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Riesgo: <span className={reportData.dark_web_security.risk_level === "critical" ? "text-red-400" : reportData.dark_web_security.risk_level === "high" ? "text-orange-400" : "text-green-400"}>{reportData.dark_web_security.risk_level?.toUpperCase()}</span>
                  </div>
                </div>
              )}

              {/* Brand Impersonation Card */}
              {reportData.brand_impersonation && (
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">🎭</span>
                    <h4 className="font-semibold text-indigo-900">Suplantación de Marca</h4>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-gray-600">Score</p>
                      <p className={`font-bold ${reportData.brand_impersonation.risk_score >= 70 ? "text-green-600" : reportData.brand_impersonation.risk_score >= 40 ? "text-orange-600" : "text-red-600"}`}>
                        {reportData.brand_impersonation.risk_score}/100
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Dominios Similares</p>
                      <p className={`font-bold ${reportData.brand_impersonation.registered_similar_domains > 0 ? "text-orange-600" : "text-green-600"}`}>
                        {reportData.brand_impersonation.registered_similar_domains}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Amenazas Activas</p>
                      <p className={`font-bold ${reportData.brand_impersonation.active_threats > 0 ? "text-red-600" : "text-green-600"}`}>
                        {reportData.brand_impersonation.active_threats}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Certificados sospechosos: {reportData.brand_impersonation.suspicious_certificates}
                  </div>
                </div>
              )}
              
              {/* Main Scans */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🔍</span>
                  <h4 className="font-semibold text-gray-900">Security Scans</h4>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-600">Total Scans</p>
                    <p className="font-bold text-gray-700">{reportData.scan_summary.total_scans}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Último Scan</p>
                    <p className="font-bold text-gray-700">
                      {reportData.scan_summary.latest_scan_date ? 
                        formatDate(reportData.scan_summary.latest_scan_date).split(',')[0] : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Top 10 Critical Vulnerabilities Table */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Top 10 Vulnerabilidades Críticas</h3>
          {reportData.vulnerability_summary.top_critical && reportData.vulnerability_summary.top_critical.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vulnerabilidad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Severidad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Objetivo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Exposicion
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.vulnerability_summary.top_critical.slice(0, 10).map((vuln, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {vuln.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getRiskLevelColor(vuln.severity)}`}>
                          {vuln.severity}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {vuln.target}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {vuln.category || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {vuln.days_exposed != null ? (
                          <span data-testid={`exec-exposure-${vuln.id}`} className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            vuln.days_exposed > 90 ? 'bg-red-100 text-red-800' :
                            vuln.days_exposed > 30 ? 'bg-orange-100 text-orange-800' :
                            vuln.days_exposed > 7 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {vuln.days_exposed}d
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-2">✅</div>
              <p>No se encontraron vulnerabilidades críticas</p>
            </div>
          )}
        </div>
        
        {/* Compliance Scores (Horizontal Bar Chart) */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Compliance Scores</h3>
          {/* OWASP Top 10: cobertura de testeo automatico (no auditoria de cumplimiento) */}
          {reportData.owasp_coverage && (
            <div className="mb-6" data-testid="owasp-coverage-block">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <h4 className="text-base font-semibold text-gray-800">
                  OWASP Top 10 (2021) — Cobertura de testeo automatizado
                </h4>
                <span className="text-xs text-gray-500">
                  Ultimo analisis web: {reportData.owasp_coverage.last_assessed ? formatDate(reportData.owasp_coverage.last_assessed) : "—"}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1 mb-3">
                {reportData.owasp_coverage.disclaimer}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  { label: "Verificables automaticamente", value: `${reportData.owasp_coverage.categories_testable}/${reportData.owasp_coverage.categories_total}`, color: "text-blue-600", testid: "owasp-kpi-testable" },
                  { label: "Con hallazgos", value: reportData.owasp_coverage.categories_with_findings, color: "text-red-600", testid: "owasp-kpi-findings" },
                  { label: "Sin hallazgos", value: reportData.owasp_coverage.categories_clean, color: "text-emerald-600", testid: "owasp-kpi-clean" },
                  { label: "No testeables externamente", value: reportData.owasp_coverage.categories_not_testable, color: "text-gray-500", testid: "owasp-kpi-nottested" },
                ].map((k) => (
                  <div key={k.label} className="p-3 bg-gray-50 rounded-lg text-center" data-testid={k.testid}>
                    <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
                    <p className="text-xs text-gray-600 mt-1">{k.label}</p>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-800 text-white text-xs uppercase tracking-wider">
                      <th className="px-3 py-2 text-left">ID</th>
                      <th className="px-3 py-2 text-left">Categoria</th>
                      <th className="px-3 py-2 text-left">Resultado</th>
                      <th className="px-3 py-2 text-left">Base de la prueba</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.owasp_coverage.categories.map((c, i) => {
                      const st = c.status === "FINDINGS"
                        ? { cls: "bg-red-100 text-red-700", label: "CON HALLAZGOS" }
                        : c.status === "NO_FINDINGS"
                        ? { cls: "bg-emerald-100 text-emerald-700", label: "SIN HALLAZGOS" }
                        : { cls: "bg-gray-200 text-gray-600", label: "NO TESTEADO" };
                      return (
                        <tr key={c.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`owasp-row-${c.id}`}>
                          <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{c.id}</td>
                          <td className="px-3 py-2 text-slate-700">{c.name_es}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${st.cls}`}>{st.label}</span>
                            {c.status === "FINDINGS" && (
                              <span className="ml-2 text-xs text-gray-600">
                                {c.findings} hallazgo(s){c.max_severity ? ` · max ${c.max_severity}` : ""}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">{c.test_basis}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {complianceData.length === 0 ? (
            <div data-testid="compliance-not-assessed" className="text-center py-10 border border-dashed border-gray-300 rounded-lg">
              <p className="text-base font-semibold text-gray-700">No evaluado</p>
              <p className="text-sm text-gray-500 mt-2 max-w-xl mx-auto">
                SegurIA no audita NIST CSF, PCI DSS ni GDPR, por lo que no publica un porcentaje
                de cumplimiento de esos marcos. El cumplimiento ISO 27001 se calcula con los
                controles cargados en el modulo ISO 27001 (SoA). De OWASP Top 10 se publica la
                cobertura de testeo automatizado (arriba), que no es una auditoria de cumplimiento.
              </p>
            </div>
          ) : (
          <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={complianceData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 100]} />
              <YAxis dataKey="name" type="category" width={80} />
              <Tooltip formatter={(value) => `${value}%`} />
              <Bar dataKey="score" fill="#3b82f6" radius={[0, 8, 8, 0]}>
                {complianceData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.score >= 70 ? '#22c55e' : entry.score >= 50 ? '#eab308' : '#ef4444'} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          
          {/* Compliance details */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
            {complianceData.map((comp, index) => (
              <div key={index} className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">{comp.name}</p>
                <p className={`text-2xl font-bold ${
                  comp.score >= 70 ? 'text-green-600' : 
                  comp.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {comp.score}%
                </p>
              </div>
            ))}
          </div>
          </>
          )}
        </div>
        
        {/* Risk Trending - Placeholder for future implementation */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Tendencia de Riesgo Histórica</h3>
          <div className="text-center py-12 text-gray-500">
            <div className="text-4xl mb-2">📈</div>
            <p>Histórico de riesgo estará disponible después de múltiples escaneos</p>
            <p className="text-sm mt-2">Esta funcionalidad se implementará en una versión futura</p>
          </div>
        </div>

        {/* Próximo Re-escaneo Sugerido */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg shadow-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Proximo Re-escaneo Sugerido</h3>
              <p className="text-gray-700">
                Basado en el nivel de riesgo <span className="font-semibold">{reportData.risk_level}</span>, 
                recomendamos re-escanear en:
              </p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-blue-600">
                {reportData.global_risk_score < 50 ? '7' : reportData.global_risk_score < 70 ? '30' : '90'}
              </p>
              <p className="text-sm text-gray-600">dias</p>
            </div>
          </div>
        </div>

        {/* Botones de Accion */}
        <div className="flex justify-center space-x-4 mt-6" data-testid="exec-report-actions">
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition-all duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleExportPDF}
            disabled={loading}
            data-testid="exec-export-pdf-btn"
          >
            <span className="text-xl mr-2">📄</span>
            {loading ? 'Generando...' : 'Exportar PDF'}
          </button>
        </div>
      </div>
    );
  };
  
  // Render Insurance Assessment Tab
  const renderInsuranceAssessment = () => {
    if (!reportData) return null;
    
    const insurability = reportData.insurability;
    const colors = getInsurabilityColor(insurability.decision);
    
    return (
      <div className="space-y-6">
        {/* Score Prominente */}
        <div className={`rounded-lg shadow-lg p-8 border-2 ${colors.border} ${colors.bg}`}>
          <div className="text-center mb-6">
            <h3 className="text-3xl font-bold mb-4" style={{ color: colors.text.replace('text-', '') }}>
              Score de Asegurabilidad
            </h3>
            <div className="text-8xl font-bold mb-4" style={{ color: colors.text.replace('text-', '') }}>
              {reportData.has_scan_data === false ? "—" : reportData.global_risk_score}
              <span className="text-4xl text-gray-500">/100</span>
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-6 mb-6">
              <div 
                className={`h-6 rounded-full transition-all duration-500 ${colors.badge}`}
                style={{ width: `${reportData.has_scan_data === false ? 0 : reportData.global_risk_score}%` }}
              ></div>
            </div>
            
            {/* Decision Badge */}
            <div className={`inline-block px-8 py-4 rounded-full font-bold text-2xl ${colors.badge} text-white shadow-lg`}>
              {insurability.decision}
            </div>
          </div>
          
          {/* Justification */}
          <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2">Justificación:</h4>
            <p className="text-gray-700">{insurability.recommendation}</p>
          </div>
        </div>
        
        {/* Métricas Clave (4 cards grandes) */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-red-500">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-3">⚠️</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Vulnerabilidades Críticas</h4>
                <p className="text-sm text-gray-600">Requieren atención inmediata</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold text-red-600">
                {reportData.vulnerability_summary.critical + reportData.vulnerability_summary.high}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                {reportData.vulnerability_summary.critical} Critical / {reportData.vulnerability_summary.high} High
              </p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-purple-500">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-3">🔍</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Exposición Externa</h4>
                <p className="text-sm text-gray-600">Información pública detectada</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold text-purple-600">
                {reportData.osint_summary.exposed_emails_count + reportData.osint_summary.subdomains_count}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                {reportData.osint_summary.exposed_emails_count} Emails / {reportData.osint_summary.subdomains_count} Subdominios
              </p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-3">🛡️</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Postura de Seguridad</h4>
                <p className="text-sm text-gray-600">Web + Network Assessment</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold text-blue-600">
                {Math.round((reportData.web_summary.average_score + 
                  (reportData.network_summary.high_risk_ports > 0 ? 50 : 100)) / 2)}%
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Web: {reportData.web_summary.average_score.toFixed(0)}% / 
                Network: {reportData.network_summary.high_risk_ports} high-risk
              </p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-cyan-500">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-3">🤖</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Defensa contra IAs</h4>
                <p className="text-sm text-gray-600">Preparación Anti-IA Maliciosa</p>
              </div>
            </div>
            <div className="text-center">
              {reportData.ai_attack_readiness ? (
                <>
                  <p className="text-5xl font-bold text-cyan-600">
                    {reportData.ai_attack_readiness.ai_readiness_score}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Score: {reportData.ai_attack_readiness.ai_readiness_score}/100
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Riesgo: {reportData.ai_attack_readiness.risk_level}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-400">
                    N/A
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    No escaneado
                  </p>
                </>
              )}
            </div>
          </div>
          
          {/* Internal Agent - Infraestructura Legacy */}
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-orange-500">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-3">🖥️</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Infraestructura Legacy</h4>
                <p className="text-sm text-gray-600">Sistemas internos on-premise</p>
              </div>
            </div>
            <div className="text-center">
              {reportData.internal_agent_summary ? (
                <>
                  <p className={`text-5xl font-bold ${reportData.internal_agent_summary.critical_count > 0 ? 'text-red-600' : reportData.internal_agent_summary.high_count > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    {reportData.internal_agent_summary.total_vulnerabilities}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    {reportData.internal_agent_summary.critical_count} Critical / {reportData.internal_agent_summary.high_count} High
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {reportData.internal_agent_summary.scanned_hosts?.length || 0} hosts escaneados
                  </p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-400">
                    N/A
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Sin agente instalado
                  </p>
                </>
              )}
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-purple-500">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-3">🛡️</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Microsegmentación de Red</h4>
                <p className="text-sm text-gray-600">Riesgo de Ransomware</p>
              </div>
            </div>
            <div className="text-center">
              {reportData.microsegmentation ? (
                <>
                  <p className="text-5xl font-bold text-purple-600">
                    {reportData.microsegmentation.microsegmentation_score}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Score: {reportData.microsegmentation.microsegmentation_score}/100
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Riesgo: {reportData.microsegmentation.risk_level}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-400">
                    N/A
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    No escaneado
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-indigo-500">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-3">🔌</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Control de Medios Extraíbles</h4>
                <p className="text-sm text-gray-600">Políticas USB/Pendrive</p>
              </div>
            </div>
            <div className="text-center">
              {reportData.usb_policy ? (
                <>
                  <p className="text-5xl font-bold text-indigo-600">
                    {reportData.usb_policy.usb_policy_score}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Score: {reportData.usb_policy.usb_policy_score}/100
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Riesgo: {reportData.usb_policy.risk_level}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-400">
                    N/A
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    No escaneado
                  </p>
                </>
              )}
            </div>
          </div>

          {/* API Security Card */}
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-3">🔌</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Seguridad de APIs</h4>
                <p className="text-sm text-gray-600">OWASP API Top 10</p>
              </div>
            </div>
            <div className="text-center">
              {reportData.api_security ? (
                <>
                  <p className={`text-5xl font-bold ${reportData.api_security.api_security_score >= 70 ? 'text-green-600' : reportData.api_security.api_security_score >= 40 ? 'text-orange-600' : 'text-red-600'}`}>
                    {reportData.api_security.api_security_score}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Score: {reportData.api_security.api_security_score}/100
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {reportData.api_security.vulnerabilities_found} vulnerabilidades / {reportData.api_security.endpoints_discovered} endpoints
                  </p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-400">
                    N/A
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    No escaneado
                  </p>
                </>
              )}
            </div>
          </div>

          {/* AI Bot Security Card */}
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-pink-500">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-3">🤖</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Seguridad de Bots IA</h4>
                <p className="text-sm text-gray-600">Chatbots y Agentes IA</p>
              </div>
            </div>
            <div className="text-center">
              {reportData.ai_bot_security ? (
                <>
                  <p className={`text-5xl font-bold ${reportData.ai_bot_security.ai_bot_security_score >= 70 ? 'text-green-600' : reportData.ai_bot_security.ai_bot_security_score >= 40 ? 'text-orange-600' : 'text-red-600'}`}>
                    {reportData.ai_bot_security.ai_bot_security_score}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Score: {reportData.ai_bot_security.ai_bot_security_score}/100
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {reportData.ai_bot_security.vulnerabilities?.length || 0} vulnerabilidades detectadas
                  </p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-400">
                    N/A
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    No escaneado
                  </p>
                </>
              )}
            </div>
          </div>
          {/* Dark Web Security Card */}
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-gray-700">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-3">🌑</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Exposición Dark Web</h4>
                <p className="text-sm text-gray-600">Credenciales y brechas</p>
              </div>
            </div>
            <div className="text-center">
              {reportData.dark_web_security ? (
                <>
                  <p className={`text-5xl font-bold ${reportData.dark_web_security.risk_score >= 70 ? "text-green-600" : reportData.dark_web_security.risk_score >= 40 ? "text-orange-600" : "text-red-600"}`}>
                    {reportData.dark_web_security.risk_score}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Score: {reportData.dark_web_security.risk_score}/100
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {reportData.dark_web_security.emails_exposed} emails expuestos / {reportData.dark_web_security.total_breaches} brechas
                  </p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-400">
                    N/A
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    No escaneado
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Brand Impersonation Card */}
          <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-indigo-500">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-3">🎭</span>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Suplantación de Marca</h4>
                <p className="text-sm text-gray-600">Dominios y phishing</p>
              </div>
            </div>
            <div className="text-center">
              {reportData.brand_impersonation ? (
                <>
                  <p className={`text-5xl font-bold ${reportData.brand_impersonation.risk_score >= 70 ? "text-green-600" : reportData.brand_impersonation.risk_score >= 40 ? "text-orange-600" : "text-red-600"}`}>
                    {reportData.brand_impersonation.risk_score}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Score: {reportData.brand_impersonation.risk_score}/100
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {reportData.brand_impersonation.active_threats} amenazas activas / {reportData.brand_impersonation.registered_similar_domains} dominios
                  </p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-400">
                    N/A
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    No escaneado
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Recomendaciones */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="text-2xl mr-2">💡</span>
            Recomendaciones Priorizadas
          </h3>
          {insurability.conditions && insurability.conditions.length > 0 ? (
            <ul className="space-y-3">
              {insurability.conditions.map((condition, index) => (
                <li key={index} className="flex items-start p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold mr-3">
                    {index + 1}
                  </span>
                  <p className="text-gray-800 flex-1">{condition}</p>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">✅</div>
              <p>No hay recomendaciones adicionales en este momento</p>
            </div>
          )}
        </div>
        
        {/* Próximo Re-escaneo Sugerido */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg shadow-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">📅 Próximo Re-escaneo Sugerido</h3>
              <p className="text-gray-700">
                Basado en el nivel de riesgo <span className="font-semibold">{reportData.risk_level}</span>, 
                recomendamos re-escanear en:
              </p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-blue-600">
                {reportData.global_risk_score < 50 ? '7' : reportData.global_risk_score < 70 ? '30' : '90'}
              </p>
              <p className="text-sm text-gray-600">días</p>
            </div>
          </div>
        </div>
        
        {/* Botones de Acción */}
        <div className="flex justify-center space-x-4">
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition-all duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleExportPDF}
            disabled={loading}
          >
            <span className="text-xl mr-2">📄</span>
            {loading ? 'Generando...' : 'Exportar PDF'}
          </button>
          <button 
            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition-all duration-200 flex items-center"
            onClick={() => alert('Funcionalidad de envío por email estará disponible en próxima versión')}
          >
            <span className="text-xl mr-2">📧</span>
            Enviar por Email
          </button>
        </div>
      </div>
    );
  };
  
  // Render Security Posture Tab
  const renderSecurityPosture = () => {
    const posture = reportData?.security_posture;
    const isSuperAdmin = currentUser?.role === 'superadmin';
    
    // Debug log
    console.log('🔍 Security Posture Debug:', {
      currentUser: currentUser,
      userRole: currentUser?.role,
      isSuperAdmin: isSuperAdmin
    });

    if (!posture) {
      return (
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <span className="text-6xl">🛡️</span>
            <h3 className="text-2xl font-bold text-gray-900 mt-4">Security Posture No Disponible</h3>
            <p className="text-gray-600 mt-2">
              Ejecuta un escaneo de "Postura de Seguridad" desde el menú Escaneos Avanzados
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Disclaimer Informativo */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <p className="text-sm text-yellow-800">
            <strong>📊 Información:</strong> Este módulo es <strong>INFORMATIVO</strong> y NO afecta el score de vulnerabilidades principal (0-100). 
            Solo SUMA puntos cuando se detectan o confirman protecciones. Score basado en detección remota + validación manual del auditor.
          </p>
        </div>

        {/* Score */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Puntuación de Postura de Seguridad</h3>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Detección Automática</p>
              <p className="text-3xl font-bold text-blue-600">{posture.automated_score}/55</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Validación Manual</p>
              <p className="text-3xl font-bold text-green-600">+{posture.manual_score}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-400">
              <p className="text-sm text-gray-600">Score Total</p>
              <p className="text-3xl font-bold text-purple-600">{posture.total_score}/95</p>
            </div>
          </div>
          
          {posture.executive_summary && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">{posture.executive_summary}</p>
            </div>
          )}
        </div>

        {/* Antivirus */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-xl font-bold text-gray-900">🛡️ Antivirus Enterprise</h4>
            <div className="flex space-x-2">
              {posture.antivirus_detected && (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                  ✓ Detectado Automáticamente
                </span>
              )}
              {posture.antivirus_manually_confirmed && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  ✓ Confirmado Manualmente
                </span>
              )}
            </div>
          </div>
          
          {posture.antivirus_detected && posture.antivirus_details.length > 0 && (
            <div className="mb-4">
              <strong className="text-sm text-gray-700">Detectado:</strong>
              <ul className="list-disc ml-6 mt-1">
                {posture.antivirus_details.map((detail, idx) => (
                  <li key={idx} className="text-sm text-gray-600">{detail}</li>
                ))}
              </ul>
            </div>
          )}
          
          {posture.antivirus_manual_details && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <strong className="text-sm text-blue-900">Validación Manual:</strong>
              <p className="text-sm text-blue-800 mt-1">{posture.antivirus_manual_details}</p>
            </div>
          )}
          
          {posture.limitations && (
            <p className="text-xs text-gray-500 mt-2">{posture.limitations}</p>
          )}
        </div>

        {/* Firewall */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-xl font-bold text-gray-900">🔥 Firewall Perimetral</h4>
            <div className="flex space-x-2">
              {posture.firewall_detected && (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                  ✓ Detectado Automáticamente
                </span>
              )}
              {posture.firewall_manually_confirmed && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  ✓ Confirmado Manualmente
                </span>
              )}
            </div>
          </div>
          
          {posture.firewall_detected && (
            <p className="text-sm text-gray-700 mb-2">
              <strong>Tipo:</strong> {posture.firewall_type} ({posture.firewall_confidence} confidence)
            </p>
          )}
          
          {posture.firewall_manual_details && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <strong className="text-sm text-blue-900">Validación Manual:</strong>
              <p className="text-sm text-blue-800 mt-1">{posture.firewall_manual_details}</p>
            </div>
          )}
        </div>

        {/* VPN */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-xl font-bold text-gray-900">🔐 VPN Corporativo</h4>
            <div className="flex space-x-2">
              {posture.vpn_detected && (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                  ✓ Detectado Automáticamente
                </span>
              )}
              {posture.vpn_manually_confirmed && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  ✓ Confirmado Manualmente
                </span>
              )}
            </div>
          </div>
          
          {posture.vpn_detected && (
            <p className="text-sm text-gray-700 mb-2">
              <strong>Tipo:</strong> {posture.vpn_type}
            </p>
          )}
          
          {posture.vpn_manual_details && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <strong className="text-sm text-blue-900">Validación Manual:</strong>
              <p className="text-sm text-blue-800 mt-1">{posture.vpn_manual_details}</p>
            </div>
          )}
        </div>

        {/* WAF/CDN */}
        {(posture.waf_detected || posture.cdn_detected) && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h4 className="text-xl font-bold text-gray-900 mb-4">🌐 WAF & CDN</h4>
            <div className="grid grid-cols-2 gap-4">
              {posture.waf_detected && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">WAF</p>
                  <p className="text-lg font-semibold text-green-900">{posture.waf_vendor}</p>
                </div>
              )}
              {posture.cdn_detected && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">CDN</p>
                  <p className="text-lg font-semibold text-blue-900">{posture.cdn_vendor}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Manual Validation Form (SuperAdmin only) */}
        {isSuperAdmin && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg shadow-lg p-6 border-2 border-purple-200">
            <h4 className="text-xl font-bold text-purple-900 mb-4">
              👤 Validación Manual (Solo SuperAdmin)
            </h4>
            
            <div className="space-y-4">
              {/* Antivirus */}
              <div className="bg-white p-4 rounded-lg">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={manualValidations.antivirus_manually_confirmed}
                    onChange={(e) => setManualValidations({
                      ...manualValidations,
                      antivirus_manually_confirmed: e.target.checked
                    })}
                    className="w-5 h-5"
                  />
                  <span className="font-semibold text-gray-900">Cliente confirmó tener Antivirus (+15 pts)</span>
                </label>
                {manualValidations.antivirus_manually_confirmed && (
                  <input
                    type="text"
                    placeholder="Detalles: Ej: Norton 360, Windows Defender..."
                    value={manualValidations.antivirus_manual_details}
                    onChange={(e) => setManualValidations({
                      ...manualValidations,
                      antivirus_manual_details: e.target.value
                    })}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                )}
              </div>

              {/* Firewall */}
              <div className="bg-white p-4 rounded-lg">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={manualValidations.firewall_manually_confirmed}
                    onChange={(e) => setManualValidations({
                      ...manualValidations,
                      firewall_manually_confirmed: e.target.checked
                    })}
                    className="w-5 h-5"
                  />
                  <span className="font-semibold text-gray-900">Cliente confirmó tener Firewall (+15 pts)</span>
                </label>
                {manualValidations.firewall_manually_confirmed && (
                  <input
                    type="text"
                    placeholder="Detalles: Ej: AWS Security Groups, Firewall corporativo..."
                    value={manualValidations.firewall_manual_details}
                    onChange={(e) => setManualValidations({
                      ...manualValidations,
                      firewall_manual_details: e.target.value
                    })}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                )}
              </div>

              {/* VPN */}
              <div className="bg-white p-4 rounded-lg">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={manualValidations.vpn_manually_confirmed}
                    onChange={(e) => setManualValidations({
                      ...manualValidations,
                      vpn_manually_confirmed: e.target.checked
                    })}
                    className="w-5 h-5"
                  />
                  <span className="font-semibold text-gray-900">Cliente confirmó usar VPN (+10 pts)</span>
                </label>
                {manualValidations.vpn_manually_confirmed && (
                  <input
                    type="text"
                    placeholder="Detalles: Ej: NordVPN, ExpressVPN, VPN corporativa..."
                    value={manualValidations.vpn_manual_details}
                    onChange={(e) => setManualValidations({
                      ...manualValidations,
                      vpn_manual_details: e.target.value
                    })}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                )}
              </div>

              {/* Auditor Notes */}
              <div className="bg-white p-4 rounded-lg">
                <label className="block font-semibold text-gray-900 mb-2">Notas del Auditor:</label>
                <textarea
                  placeholder="Notas adicionales, evidencias mencionadas por el cliente, capturas compartidas, etc..."
                  value={manualValidations.auditor_notes}
                  onChange={(e) => setManualValidations({
                    ...manualValidations,
                    auditor_notes: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  rows="3"
                />
              </div>

              <button
                onClick={saveManualValidations}
                disabled={savingValidations}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg disabled:bg-gray-400"
              >
                {savingValidations ? 'Guardando...' : '💾 Guardar Validaciones Manuales'}
              </button>
            </div>
          </div>
        )}

        {posture.auditor_notes && (
          <div className="bg-gray-50 rounded-lg p-4">
            <strong className="text-gray-900">Notas del Auditor:</strong>
            <p className="text-gray-700 mt-1 whitespace-pre-line">{posture.auditor_notes}</p>
          </div>
        )}
      </div>
    );
  };

  // ========================================================================
  // RENDER AI DEFENSE TAB
  // ========================================================================
  const renderAIDefense = () => {
    if (!reportData || !reportData.ai_attack_readiness) {
      return (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="text-8xl mb-6">🤖</div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Sin Datos de Defensa contra IAs
          </h3>
          <p className="text-gray-600 text-lg mb-8">
            No se ha ejecutado un escaneo de AI-Attack Readiness aún.
          </p>
          <p className="text-gray-500 mb-4">
            Este escaneo evalúa vulnerabilidades específicamente explotables por IAs maliciosas como FraudGPT, WormGPT, PoisonGPT y XXXGPT.
          </p>
          <button
            onClick={() => setParentActiveTab && setParentActiveTab('ai-attack-readiness')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200"
          >
            🚀 Ejecutar Escaneo AI-Attack Readiness
          </button>
        </div>
      );
    }

    const aiData = reportData.ai_attack_readiness;
    
    const getRiskColor = (score) => {
      if (score >= 80) return { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', badge: 'bg-green-500' };
      if (score >= 60) return { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-500' };
      if (score >= 40) return { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700', badge: 'bg-orange-500' };
      return { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', badge: 'bg-red-500' };
    };

    const getRiskLabel = (riskLevel) => {
      const labels = {
        'LOW': '✅ BAJO RIESGO - Bien Protegido',
        'MEDIUM': '⚠️ RIESGO MEDIO - Gaps de Seguridad',
        'HIGH': '🔴 ALTO RIESGO - Vulnerable a Ataques IA',
        'CRITICAL': '🚨 RIESGO CRÍTICO - Extremadamente Vulnerable'
      };
      return labels[riskLevel] || riskLevel;
    };

    const colors = getRiskColor(aiData.ai_readiness_score);

    return (
      <div className="space-y-8">
        {/* Score Principal */}
        <div className={`rounded-lg shadow-xl p-8 border-2 ${colors.border} ${colors.bg}`}>
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Preparación contra Ataques IA
            </h2>
            <div className={`text-9xl font-bold mb-4 ${colors.text}`}>
              {aiData.ai_readiness_score}
              <span className="text-4xl text-gray-500">/100</span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-8 mb-6">
              <div
                className={`h-8 rounded-full transition-all duration-500 ${colors.badge} flex items-center justify-end pr-4`}
                style={{ width: `${aiData.ai_readiness_score}%` }}
              >
                <span className="text-white font-bold text-sm">{aiData.ai_readiness_score}%</span>
              </div>
            </div>

            {/* Risk Badge */}
            <div className={`inline-block px-8 py-4 rounded-full font-bold text-2xl ${colors.badge} text-white shadow-lg`}>
              {getRiskLabel(aiData.risk_level)}
            </div>

            <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600">
                <strong>Objetivo:</strong> {aiData.target} | 
                <strong className="ml-4">Scan Date:</strong> {new Date(aiData.scan_timestamp).toLocaleString('es-AR')}
              </p>
            </div>
          </div>
        </div>

        {/* Attack Vectors */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="text-3xl mr-3">🎯</span>
            Vectores de Ataque IA
          </h3>
          <div className="space-y-4">
            {Object.entries(aiData.attack_vectors).map(([vector, score]) => {
              const maxScores = {
                phishing_surface: 40,
                injection_points: 30,
                auth_weaknesses: 40,
                info_disclosure: 30,
                automation_friendly: 60
              };
              const max = maxScores[vector] || 100;
              const percentage = Math.min(100, (score / max) * 100);
              const isHigh = percentage > 60;

              const vectorNames = {
                phishing_surface: '🎣 Superficie de Phishing (FraudGPT)',
                injection_points: '💉 Puntos de Inyección (WormGPT)',
                auth_weaknesses: '🔐 Debilidades de Autenticación',
                info_disclosure: '📄 Divulgación de Información',
                automation_friendly: '🤖 Facilidad de Automatización'
              };

              return (
                <div key={vector} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">{vectorNames[vector]}</span>
                    <span className={`font-bold ${isHigh ? 'text-red-600' : 'text-green-600'}`}>
                      {score}/{max}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className={`h-4 rounded-full transition-all duration-500 ${isHigh ? 'bg-red-500' : 'bg-green-500'}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top 10 Vulnerabilidades */}
        {aiData.vulnerabilities && aiData.vulnerabilities.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-3xl mr-3">⚠️</span>
              Top 10 Vulnerabilidades Detectadas
            </h3>
            <div className="space-y-4">
              {aiData.vulnerabilities.slice(0, 10).map((vuln, index) => {
                const severityColors = {
                  'critical': 'bg-red-100 border-red-300 text-red-800',
                  'high': 'bg-orange-100 border-orange-300 text-orange-800',
                  'medium': 'bg-yellow-100 border-yellow-300 text-yellow-800',
                  'low': 'bg-blue-100 border-blue-300 text-blue-800'
                };
                const colorClass = severityColors[vuln.severity?.toLowerCase()] || 'bg-gray-100 border-gray-300 text-gray-800';

                return (
                  <div key={index} className={`border-2 rounded-lg p-4 ${colorClass}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 rounded text-xs font-bold">
                            {vuln.severity?.toUpperCase()}
                          </span>
                          <h4 className="font-bold">{vuln.title}</h4>
                        </div>
                        <p className="text-sm mb-2">{vuln.description?.substring(0, 200)}...</p>
                        {vuln.recommendation && (
                          <p className="text-sm italic">💡 {vuln.recommendation?.substring(0, 150)}...</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recomendaciones Top 5 */}
        {aiData.recommendations && aiData.recommendations.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-3xl mr-3">💡</span>
              Top 5 Recomendaciones Priorizadas
            </h3>
            <div className="space-y-4">
              {aiData.recommendations.slice(0, 5).map((rec, index) => {
                const priorityColors = {
                  'CRITICAL': 'bg-red-500',
                  'HIGH': 'bg-orange-500',
                  'MEDIUM': 'bg-yellow-500',
                  'LOW': 'bg-blue-500'
                };
                return (
                  <div key={index} className="border-2 border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      <span className={`flex-shrink-0 w-10 h-10 ${priorityColors[rec.priority]} text-white rounded-full flex items-center justify-center font-bold`}>
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 ${priorityColors[rec.priority]} text-white rounded text-xs font-bold`}>
                            {rec.priority}
                          </span>
                          <h4 className="font-bold text-gray-900">{rec.title}</h4>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{rec.description}</p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="bg-blue-50 p-2 rounded">
                            <strong>Timeline:</strong> {rec.timeline}
                          </div>
                          <div className="bg-green-50 p-2 rounded">
                            <strong>Costo:</strong> {rec.cost}
                          </div>
                          <div className="bg-purple-50 p-2 rounded">
                            <strong>Impacto:</strong> {rec.ai_defense_impact}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Contexto de Amenazas */}
        {aiData.threat_intelligence && (
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg shadow-lg p-6 border-2 border-purple-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="text-3xl mr-3">🚨</span>
              Contexto de Amenazas IA Actuales
            </h3>
            {aiData.threat_intelligence.active_ai_threats && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {aiData.threat_intelligence.active_ai_threats.slice(0, 4).map((threat, index) => (
                  <div key={index} className="bg-white rounded-lg p-4">
                    <h5 className="font-bold text-purple-900 mb-1">{threat.name}</h5>
                    <p className="text-sm text-gray-700 mb-2">{threat.capability}</p>
                    <p className="text-xs text-gray-600">Precio: {threat.price}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ========================================================================
  // RENDER MICROSEGMENTATION TAB
  // ========================================================================
  const renderMicrosegmentation = () => {
    if (!reportData || !reportData.microsegmentation) {
      return (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="text-8xl mb-6">🛡️</div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Sin Datos de Microsegmentación
          </h3>
          <p className="text-gray-600 text-lg mb-8">
            No se ha ejecutado un escaneo de Microsegmentación de Red aún.
          </p>
          <p className="text-gray-500 mb-4">
            Este escaneo evalúa el nivel de segmentación de red y el riesgo de propagación lateral de ransomware.
          </p>
          <button
            onClick={() => setParentActiveTab && setParentActiveTab('microsegmentation')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200"
          >
            🚀 Ejecutar Escaneo de Microsegmentación
          </button>
        </div>
      );
    }

    const microData = reportData.microsegmentation;
    
    const getRiskColor = (score) => {
      if (score >= 80) return { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', badge: 'bg-green-500' };
      if (score >= 60) return { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-500' };
      if (score >= 40) return { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700', badge: 'bg-orange-500' };
      return { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', badge: 'bg-red-500' };
    };

    const getRiskLabel = (riskLevel) => {
      const labels = {
        'LOW': '✅ BAJO RIESGO - Red Segmentada',
        'MEDIUM': '⚠️ RIESGO MEDIO - Segmentación Parcial',
        'HIGH': '🔴 ALTO RIESGO - Red Plana Detectada',
        'CRITICAL': '🚨 RIESGO CRÍTICO - Servicios Críticos Expuestos'
      };
      return labels[riskLevel] || riskLevel;
    };

    const colors = getRiskColor(microData.microsegmentation_score);

    return (
      <div className="space-y-8">
        {/* Score Principal */}
        <div className={`rounded-lg shadow-xl p-8 border-2 ${colors.border} ${colors.bg}`}>
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nivel de Segmentación de Red
            </h2>
            <div className={`text-9xl font-bold mb-4 ${colors.text}`}>
              {microData.microsegmentation_score}
              <span className="text-4xl text-gray-500">/100</span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-8 mb-6">
              <div
                className={`h-8 rounded-full transition-all duration-500 ${colors.badge} flex items-center justify-end pr-4`}
                style={{ width: `${microData.microsegmentation_score}%` }}
              >
                <span className="text-white font-bold text-sm">{microData.microsegmentation_score}%</span>
              </div>
            </div>

            {/* Risk Badge */}
            <div className={`inline-block px-8 py-4 rounded-full font-bold text-2xl ${colors.badge} text-white shadow-lg`}>
              {getRiskLabel(microData.risk_level)}
            </div>

            {/* Ransomware Risk */}
            {microData.ransomware_propagation_probability && (
              <div className="mt-6 bg-red-50 border-2 border-red-300 rounded-lg p-6">
                <h3 className="text-xl font-bold text-red-900 mb-4">🔴 Riesgo de Propagación de Ransomware</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-sm text-red-600 mb-1">Probabilidad</p>
                    <p className="text-2xl font-bold text-red-900">{microData.ransomware_propagation_probability.probability_range}</p>
                  </div>
                  <div>
                    <p className="text-sm text-red-600 mb-1">Tiempo Contención</p>
                    <p className="text-lg font-bold text-red-900">{microData.ransomware_propagation_probability.containment_time}</p>
                  </div>
                  <div>
                    <p className="text-sm text-red-600 mb-1">Pérdida Potencial</p>
                    <p className="text-lg font-bold text-red-900">{microData.ransomware_propagation_probability.potential_loss}</p>
                  </div>
                  <div>
                    <p className="text-sm text-red-600 mb-1">Costo Estimado</p>
                    <p className="text-lg font-bold text-red-900">{microData.ransomware_propagation_probability.cost_estimate}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Análisis de Segmentación */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="text-3xl mr-3">🎯</span>
            Análisis de Segmentación
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Open Ports */}
            {microData.detailed_results?.open_ports_count !== undefined && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🔌</span>
                  <span className="font-semibold text-gray-900">Puertos Abiertos</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {microData.detailed_results.open_ports_count}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {microData.detailed_results.port_exposure?.assessment || "Analyzing..."}
                </p>
              </div>
            )}

            {/* Internal Services */}
            {microData.detailed_results?.exposed_internal_services && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🏢</span>
                  <span className="font-semibold text-gray-900">Servicios Internos Expuestos</span>
                </div>
                <p className={`text-3xl font-bold ${microData.detailed_results.exposed_internal_services.count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {microData.detailed_results.exposed_internal_services.count}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {microData.detailed_results.exposed_internal_services.risk_level || "LOW"}
                </p>
              </div>
            )}

            {/* DMZ Architecture */}
            {microData.detailed_results?.dmz_architecture && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🌐</span>
                  <span className="font-semibold text-gray-900">Arquitectura</span>
                </div>
                <p className={`text-lg font-bold ${microData.detailed_results.dmz_architecture.dmz_detected ? 'text-green-600' : 'text-red-600'}`}>
                  {microData.detailed_results.dmz_architecture.architecture || "Unknown"}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {microData.detailed_results.dmz_architecture.dmz_detected ? "DMZ Detectada" : "Red Plana"}
                </p>
              </div>
            )}

            {/* Firewall */}
            {microData.detailed_results?.firewall_detection && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🔥</span>
                  <span className="font-semibold text-gray-900">Firewall</span>
                </div>
                <p className={`text-lg font-bold ${microData.detailed_results.firewall_detection.firewall_detected ? 'text-green-600' : 'text-red-600'}`}>
                  {microData.detailed_results.firewall_detection.assessment || "Unknown"}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {microData.detailed_results.firewall_detection.closed_critical_ports || 0} puertos críticos cerrados
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Top 10 Vulnerabilidades */}
        {microData.vulnerabilities && microData.vulnerabilities.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-3xl mr-3">⚠️</span>
              Top 10 Vulnerabilidades Detectadas
            </h3>
            <div className="space-y-4">
              {microData.vulnerabilities.slice(0, 10).map((vuln, index) => {
                const severityColors = {
                  'CRITICAL': 'bg-red-100 border-red-300 text-red-800',
                  'HIGH': 'bg-orange-100 border-orange-300 text-orange-800',
                  'MEDIUM': 'bg-yellow-100 border-yellow-300 text-yellow-800',
                  'LOW': 'bg-blue-100 border-blue-300 text-blue-800'
                };
                const colorClass = severityColors[vuln.severity?.toUpperCase()] || 'bg-gray-100 border-gray-300 text-gray-800';

                return (
                  <div key={index} className={`border-2 rounded-lg p-4 ${colorClass}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 rounded text-xs font-bold">
                            {vuln.severity?.toUpperCase()}
                          </span>
                          <h4 className="font-bold">{vuln.title}</h4>
                        </div>
                        <p className="text-sm mb-2">{vuln.description?.substring(0, 200)}...</p>
                        {vuln.recommendation && (
                          <p className="text-sm italic">💡 {vuln.recommendation?.substring(0, 150)}...</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Top 5 Recomendaciones */}
        {microData.recommendations && microData.recommendations.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-3xl mr-3">💡</span>
              Top 5 Recomendaciones Priorizadas
            </h3>
            <div className="space-y-4">
              {microData.recommendations.slice(0, 5).map((rec, index) => {
                const priorityColors = {
                  'CRITICAL': 'bg-red-500',
                  'HIGH': 'bg-orange-500',
                  'MEDIUM': 'bg-yellow-500',
                  'LOW': 'bg-blue-500'
                };
                return (
                  <div key={index} className="border-2 border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      <span className={`flex-shrink-0 w-10 h-10 ${priorityColors[rec.priority]} text-white rounded-full flex items-center justify-center font-bold`}>
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 ${priorityColors[rec.priority]} text-white rounded text-xs font-bold`}>
                            {rec.priority}
                          </span>
                          <h4 className="font-bold text-gray-900">{rec.title}</h4>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{rec.description}</p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="bg-blue-50 p-2 rounded">
                            <strong>Timeline:</strong> {rec.timeline}
                          </div>
                          <div className="bg-green-50 p-2 rounded">
                            <strong>Costo:</strong> {rec.cost}
                          </div>
                          <div className="bg-purple-50 p-2 rounded">
                            <strong>Impacto:</strong> {rec.ai_defense_impact || rec.impact || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ========================================================================
  // RENDER USB POLICY TAB
  // ========================================================================
  const renderUSBPolicy = () => {
    if (!reportData || !reportData.usb_policy) {
      return (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="text-8xl mb-6">🔌</div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Sin Datos de Control de Medios Extraíbles
          </h3>
          <p className="text-gray-600 text-lg mb-8">
            No se ha ejecutado una evaluación de políticas USB/Pendrive aún.
          </p>
          <p className="text-gray-500 mb-4">
            Este escaneo evalúa controles sobre dispositivos USB, políticas públicas, certificaciones y DLP.
          </p>
          <button
            onClick={() => setParentActiveTab && setParentActiveTab('usb-policy')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200"
          >
            🚀 Ejecutar Evaluación de Medios Extraíbles
          </button>
        </div>
      );
    }

    const usbData = reportData.usb_policy;
    
    const getRiskColor = (score) => {
      if (score >= 80) return { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', badge: 'bg-green-500' };
      if (score >= 60) return { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-500' };
      if (score >= 40) return { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700', badge: 'bg-orange-500' };
      return { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', badge: 'bg-red-500' };
    };

    const getRiskLabel = (riskLevel) => {
      const labels = {
        'LOW': '✅ BAJO RIESGO - Controles Implementados',
        'MEDIUM': '⚠️ RIESGO MEDIO - Controles Parciales',
        'HIGH': '🔴 ALTO RIESGO - Controles Insuficientes',
        'CRITICAL': '🚨 RIESGO CRÍTICO - Sin Controles'
      };
      return labels[riskLevel] || riskLevel;
    };

    const colors = getRiskColor(usbData.usb_policy_score);

    return (
      <div className="space-y-8">
        {/* Score Principal */}
        <div className={`rounded-lg shadow-xl p-8 border-2 ${colors.border} ${colors.bg}`}>
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nivel de Control de Medios Extraíbles
            </h2>
            <div className={`text-9xl font-bold mb-4 ${colors.text}`}>
              {usbData.usb_policy_score}
              <span className="text-4xl text-gray-500">/100</span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-8 mb-6">
              <div
                className={`h-8 rounded-full transition-all duration-500 ${colors.badge} flex items-center justify-end pr-4`}
                style={{ width: `${usbData.usb_policy_score}%` }}
              >
                <span className="text-white font-bold text-sm">{usbData.usb_policy_score}%</span>
              </div>
            </div>

            {/* Risk Badge */}
            <div className={`inline-block px-8 py-4 rounded-full font-bold text-2xl ${colors.badge} text-white shadow-lg`}>
              {getRiskLabel(usbData.risk_level)}
            </div>
          </div>
        </div>

        {/* Detailed Results */}
        {usbData.detailed_results && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-3xl mr-3">📋</span>
              Análisis de Controles
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {usbData.detailed_results.public_policies && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">📄</span>
                    <span className="font-semibold text-gray-900">Políticas Públicas USB</span>
                  </div>
                  <p className={`text-lg font-bold ${usbData.detailed_results.public_policies.usb_policy_found ? 'text-green-600' : 'text-red-600'}`}>
                    {usbData.detailed_results.public_policies.usb_policy_found ? 'Detectadas' : 'No Detectadas'}
                  </p>
                </div>
              )}

              {usbData.detailed_results.certifications && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">🏆</span>
                    <span className="font-semibold text-gray-900">ISO 27001</span>
                  </div>
                  <p className={`text-lg font-bold ${usbData.detailed_results.certifications.iso27001 ? 'text-green-600' : 'text-red-600'}`}>
                    {usbData.detailed_results.certifications.iso27001 ? 'Verificada' : 'No Verificada'}
                  </p>
                </div>
              )}

              {usbData.detailed_results.dlp_presence && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">🛡️</span>
                    <span className="font-semibold text-gray-900">Solución DLP</span>
                  </div>
                  <p className={`text-lg font-bold ${usbData.detailed_results.dlp_presence.dlp_detected ? 'text-green-600' : 'text-red-600'}`}>
                    {usbData.detailed_results.dlp_presence.dlp_detected ? 'Detectada' : 'No Detectada'}
                  </p>
                </div>
              )}

              {usbData.detailed_results.technology_stack && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">💻</span>
                    <span className="font-semibold text-gray-900">MDM/EMM</span>
                  </div>
                  <p className={`text-lg font-bold ${usbData.detailed_results.technology_stack.mdm_detected ? 'text-green-600' : 'text-red-600'}`}>
                    {usbData.detailed_results.technology_stack.mdm_detected ? 'Detectado' : 'No Detectado'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Top 5 Vulnerabilities */}
        {usbData.vulnerabilities && usbData.vulnerabilities.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-3xl mr-3">⚠️</span>
              Top 5 Vulnerabilidades Detectadas
            </h3>
            <div className="space-y-4">
              {usbData.vulnerabilities.slice(0, 5).map((vuln, index) => {
                const severityColors = {
                  'CRITICAL': 'bg-red-100 border-red-300 text-red-800',
                  'HIGH': 'bg-orange-100 border-orange-300 text-orange-800',
                  'MEDIUM': 'bg-yellow-100 border-yellow-300 text-yellow-800',
                  'LOW': 'bg-blue-100 border-blue-300 text-blue-800'
                };
                const colorClass = severityColors[vuln.severity?.toUpperCase()] || 'bg-gray-100 border-gray-300 text-gray-800';

                return (
                  <div key={index} className={`border-2 rounded-lg p-4 ${colorClass}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 rounded text-xs font-bold">
                            {vuln.severity?.toUpperCase()}
                          </span>
                          <h4 className="font-bold">{vuln.title}</h4>
                        </div>
                        <p className="text-sm mb-2">{vuln.description?.substring(0, 200)}...</p>
                        {vuln.recommendation && (
                          <p className="text-sm italic">💡 {vuln.recommendation?.substring(0, 150)}...</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Top 3 Recommendations */}
        {usbData.recommendations && usbData.recommendations.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-3xl mr-3">💡</span>
              Top 3 Recomendaciones Priorizadas
            </h3>
            <div className="space-y-4">
              {usbData.recommendations.slice(0, 3).map((rec, index) => {
                const priorityColors = {
                  'CRITICAL': 'bg-red-500',
                  'HIGH': 'bg-orange-500',
                  'MEDIUM': 'bg-yellow-500',
                  'LOW': 'bg-blue-500'
                };
                return (
                  <div key={index} className="border-2 border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      <span className={`flex-shrink-0 w-10 h-10 ${priorityColors[rec.priority]} text-white rounded-full flex items-center justify-center font-bold`}>
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 ${priorityColors[rec.priority]} text-white rounded text-xs font-bold`}>
                            {rec.priority}
                          </span>
                          <h4 className="font-bold text-gray-900">{rec.title}</h4>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{rec.description}</p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="bg-blue-50 p-2 rounded">
                            <strong>Timeline:</strong> {rec.timeline}
                          </div>
                          <div className="bg-green-50 p-2 rounded">
                            <strong>Costo:</strong> {rec.cost}
                          </div>
                          <div className="bg-purple-50 p-2 rounded">
                            <strong>Impacto:</strong> {rec.impact || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Compliance Gaps */}
        {usbData.compliance_gaps && usbData.compliance_gaps.length > 0 && (
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg shadow-lg p-6 border-2 border-red-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="text-3xl mr-3">📊</span>
              Brechas de Cumplimiento
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {usbData.compliance_gaps.map((gap, index) => (
                <div key={index} className="bg-white rounded-lg p-3 border border-red-200">
                  <p className="text-sm text-red-900 font-medium">🔴 {gap}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };
  // ========================================================================
  // MAIN RENDER
  // ========================================================================
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-xl font-semibold text-gray-700">Cargando Reporte Ejecutivo...</p>
          <p className="text-sm text-gray-500 mt-2">Consolidando datos de todos los módulos</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-lg text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-red-800 mb-2">Error al Cargar Reporte</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <button 
            onClick={fetchReportData}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-200"
          >
            🔄 Reintentar
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">📊 Reporte Ejecutivo</h1>
        <p className="text-blue-100">Análisis consolidado de seguridad y evaluación de asegurabilidad</p>
      </div>
      
      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-lg p-4">
        <div className="flex space-x-4 border-b border-gray-200">
          <button 
            onClick={() => setActiveTab('technical')} 
            className={`pb-4 px-6 font-medium text-lg transition-all duration-200 ${
              activeTab === 'technical' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="text-xl mr-2">🔬</span>
            Technical Report
          </button>
          <button 
            onClick={() => setActiveTab('underwriting')}
            className={`pb-4 px-6 font-medium text-lg transition-all duration-200 ${
              activeTab === 'underwriting' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="underwriting-tab"
          >
            <span className="text-xl mr-2">📄</span>
            Reporte de Underwriting Cyber
          </button>
          <button 
            onClick={handlePyMEReport}
            disabled={pymeLoading}
            className="pb-4 px-6 font-medium text-lg transition-all duration-200 text-gray-500 hover:text-green-700"
            data-testid="pyme-report-btn"
          >
            <span className="text-xl mr-2">📋</span>
            {pymeLoading ? 'Generando...' : 'Informe PyME (PDF)'}
          </button>
          <button 
            onClick={() => setActiveTab('ver')}
            className={`pb-4 px-6 font-medium text-lg transition-all duration-200 ${
              activeTab === 'ver' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="ver-tab-executive"
          >
            <span className="text-xl mr-2">💰</span>
            VER (Valor Económico)
          </button>
          <button
            onClick={() => setActiveTab('attack')}
            className={`pb-4 px-6 font-medium text-lg transition-all duration-200 ${
              activeTab === 'attack'
                ? 'text-red-600 border-b-2 border-red-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="attack-tab-executive"
          >
            <span className="text-xl mr-2">🎯</span>
            MITRE ATT&CK
          </button>
        </div>
      </div>
      
      {/* Content */}
      {activeTab === 'technical' && renderTechnicalReport()}
      {activeTab === 'underwriting' && <UnderwritingReport />}
      {activeTab === 'posture' && renderSecurityPosture()}
      {activeTab === 'ai-defense' && renderAIDefense()}
      {activeTab === 'microsegmentation' && renderMicrosegmentation()}
      {activeTab === 'usb-policy' && renderUSBPolicy()}
      {activeTab === 'ver' && VerModule && <VerModule />}
      {activeTab === 'attack' && <AttackProfilePanel />}
    </div>
  );
};


// ---------- Panel MITRE ATT&CK Profile ----------
const TACTIC_LABELS = TACTIC_ORDER_ES;

const AttackProfilePanel = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [mode, setMode] = React.useState('real');  // 'real' | 'demo'
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadData = React.useCallback((useDemoMode) => {
    setLoading(true); setError(null);
    const token = localStorage.getItem('token');
    const endpoint = useDemoMode ? '/api/mitre-attack/preview-demo' : '/api/mitre-attack/enrich-client';
    fetch(`${API_URL}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, [API_URL]);

  React.useEffect(() => { loadData(mode === 'demo'); }, [mode, loadData]);

  if (loading) return <div className="mt-6 p-8 bg-white rounded-lg text-center text-gray-500">Cargando perfil MITRE ATT&CK…</div>;
  if (error) return <div className="mt-6 p-6 bg-red-50 border border-red-200 rounded-lg text-red-800">Error: {error}</div>;
  if (!data) return null;

  const profile = data.profile || {};
  const exposed = profile.tactics_exposed || {};
  const top = profile.top_techniques || [];

  return (
    <div className="mt-6 bg-white shadow rounded-lg p-6" data-testid="attack-profile-panel">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Perfil MITRE ATT&CK — Superficie de Amenazas</h2>
          <div className="text-xs text-gray-500 italic mt-1">Framework MITRE ATT&CK Enterprise · mapeo automático vía CWE</div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <div className="inline-flex rounded-lg border border-gray-300 bg-gray-50 p-1">
            <button
              onClick={() => setMode('real')}
              className={`px-3 py-1 text-sm font-medium rounded transition ${mode==='real' ? 'bg-white shadow text-blue-700' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="attack-mode-real"
            >Mi organización</button>
            <button
              onClick={() => setMode('demo')}
              className={`px-3 py-1 text-sm font-medium rounded transition ${mode==='demo' ? 'bg-white shadow text-orange-700' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="attack-mode-demo"
            >Ver ejemplo (5 CVEs KEV)</button>
          </div>
          {mode === 'demo' && (
            <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-1 max-w-xs text-right">
              Datos de demostración con CVEs famosos (Log4Shell, PAN-OS, MOVEit, Fortinet). No refleja escaneos de su cuenta.
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
          <div className="text-xs text-red-700 uppercase font-bold">CVEs mapeadas a ATT&CK</div>
          <div className="text-3xl font-bold text-red-800 mt-1" data-testid="attack-cves-mapped">{profile.total_cves_mapped_to_attack || 0}</div>
          <div className="text-xs text-gray-600 mt-1">de {profile.total_cves_analyzed || 0} CVEs totales del inventario</div>
        </div>
        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
          <div className="text-xs text-orange-700 uppercase font-bold">Técnicas distintas</div>
          <div className="text-3xl font-bold text-orange-800 mt-1" data-testid="attack-distinct-techniques">{profile.distinct_techniques || 0}</div>
          <div className="text-xs text-gray-600 mt-1">técnicas ATT&CK activas en la superficie</div>
        </div>
        <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
          <div className="text-xs text-purple-700 uppercase font-bold">Tácticas expuestas</div>
          <div className="text-3xl font-bold text-purple-800 mt-1" data-testid="attack-tactics-count">{Object.keys(exposed).length}</div>
          <div className="text-xs text-gray-600 mt-1">de 14 tácticas del framework</div>
        </div>
      </div>

      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">Matriz de tácticas</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2 mb-6">
        {TACTIC_LABELS.map(([sn, label]) => {
          const n = exposed[sn] || 0;
          const active = n > 0;
          return (
            <div key={sn} className={`p-2 rounded text-center text-xs ${active ? 'bg-red-100 border border-red-400 text-red-900 font-semibold' : 'bg-gray-50 border border-gray-200 text-gray-400'}`} data-testid={`attack-tactic-${sn}`}>
              <div>{label}</div>
              {active && <div className="mt-1 inline-block bg-red-600 text-white rounded-full px-2 text-[10px] font-bold">{n}</div>}
            </div>
          );
        })}
      </div>

      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">Top técnicas expuestas</h3>
      {top.length === 0 ? (
        <div className="p-6 bg-blue-50 border border-blue-200 rounded text-sm text-gray-700" data-testid="attack-empty-state">
          {profile.total_cves_analyzed > 0 ? (
            <>
              <div className="font-semibold text-blue-900 mb-2">📭 Sus {profile.total_cves_analyzed} CVE(s) actuales no son mapeables a técnicas ATT&CK</div>
              <div className="mb-3">Cada CVE se mapea a técnicas ATT&CK a través de su CWE. Los CVEs que su inventario tiene hoy no tienen CWE resuelto en NVD o su CWE está fuera del mapping curado. Ejecute más escaneos desde <b>Escaneos Avanzados</b> para poblar el perfil.</div>
              <div className="text-xs text-blue-800">💡 Active arriba <b>"Ver ejemplo (5 CVEs KEV)"</b> para explorar cómo se verá este panel cuando tenga datos mapeables.</div>
            </>
          ) : (
            <>
              <div className="font-semibold text-blue-900 mb-2">📭 Su inventario aún no tiene vulnerabilidades con CVE detectadas</div>
              <div className="mb-3">Para poblar este perfil ATT&CK debe correr al menos un escaneo que descubra vulnerabilidades conocidas (CVEs). Ejecute desde el menú <b>Escaneos Avanzados</b>.</div>
              <div className="text-xs text-blue-800">💡 Mientras tanto, puede activar arriba <b>"Ver ejemplo (5 CVEs KEV)"</b> para explorar cómo se verá este panel cuando tenga datos reales.</div>
            </>
          )}
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="p-2">Técnica</th>
              <th className="p-2">Nombre</th>
              <th className="p-2">Tácticas</th>
              <th className="p-2 text-right">CVEs</th>
            </tr>
          </thead>
          <tbody>
            {top.map(t => (
              <tr key={t.mitre_id} className="border-b">
                <td className="p-2 font-mono text-red-700 font-bold">{t.mitre_id}</td>
                <td className="p-2">{techniqueEs(t.mitre_id, t.name)}</td>
                <td className="p-2 text-xs text-gray-600">{tacticsEs(t.tactics)}</td>
                <td className="p-2 text-right font-bold">{t.cve_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-6 text-xs text-gray-500 italic">
        Fuente: MITRE ATT&CK Enterprise · mapeo CVE → CWE (NVD API) → Technique. Las técnicas resaltadas indican vectores de ataque activos en la superficie del cliente y forman el perfil actuarial usado en el underwriting cyber.
      </div>
    </div>
  );
};

export default ExecutiveReport;
